/**
 * FamilyMigrationMap — Modal with Leaflet map of birthplaces + homes + places
 * of death across every member of a tree.
 *
 * Approach:
 *   1. Fetch /family-trees/:id/places → aggregated place strings
 *   2. Geocode each unique string via Nominatim (free), cached in
 *      localStorage (geocoded strings are repeated across generations).
 *   3. Plot pins coloured by kind (birth/home/death).
 *
 * Keeps the pod memory flat (no server-side geocoding → no new HTTP egress).
 */
import { useEffect, useRef, useState } from "react";
import { X, Loader2, MapPin } from "lucide-react";
import { http } from "@/lib/api";
import "leaflet/dist/leaflet.css";

const P = {
  ink: "#431a04",
  inkSoft: "#8a5a2e",
  saffron: "#C2410C",
  rose: "#B23A3A",
  teal: "#2D6B4F",
  saffronBg: "#FFF7ED",
  parchment: "#FFFBF4",
  line: "rgba(194,65,12,0.15)",
};

const KIND_COLOR = {
  birth: P.saffron,
  home:  P.teal,
  death: P.rose,
};

const CACHE_KEY = "ift_geocode_cache_v1";

function loadCache() {
  try { return JSON.parse(localStorage.getItem(CACHE_KEY) || "{}"); }
  catch { return {}; }
}
function saveCache(c) {
  try { localStorage.setItem(CACHE_KEY, JSON.stringify(c)); } catch {}
}

async function geocodeOnce(label) {
  const cache = loadCache();
  const key = label.trim().toLowerCase();
  if (cache[key] !== undefined) return cache[key];
  try {
    // Nominatim: 1 req/sec. We rate-limit via the caller.
    // Bias toward India when the label mentions India / an Indian state, so
    // "Pala, Kottayam" doesn't mis-match to Pala in Spain.
    const indiaBias = /\b(india|kerala|tamil|karnataka|andhra|telangana|gujarat|maharashtra|punjab|haryana|rajasthan|uttar|bihar|bengal|odisha|assam|goa|mp|up|delhi|mumbai|chennai|kolkata|bengaluru|kochi|thrissur|kottayam|ernakulam|trivandrum|thiruvananthapuram)\b/i.test(label);
    const cc = indiaBias ? "&countrycodes=in" : "";
    const url = `https://nominatim.openstreetmap.org/search?format=json&limit=1${cc}&q=${encodeURIComponent(label)}`;
    const r = await fetch(url, { headers: { "Accept-Language": "en" } });
    if (!r.ok) throw new Error("geocode HTTP " + r.status);
    const data = await r.json();
    const hit = Array.isArray(data) && data[0];
    const result = hit ? { lat: parseFloat(hit.lat), lon: parseFloat(hit.lon), display_name: hit.display_name } : null;
    cache[key] = result;
    saveCache(cache);
    return result;
  } catch {
    cache[key] = null;
    saveCache(cache);
    return null;
  }
}

export default function FamilyMigrationMap({ treeId, treeName, onClose }) {
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const [places, setPlaces] = useState(null);  // null=loading, []=none
  const [pins, setPins] = useState([]);        // [{...place, lat, lon}]
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [error, setError] = useState("");

  // Fetch place list
  useEffect(() => {
    let cancel = false;
    http.get(`/family-trees/${treeId}/places`)
      .then((r) => { if (!cancel) setPlaces(r.data?.places || []); })
      .catch(() => { if (!cancel) setError("Could not load places for this tree."); });
    return () => { cancel = true; };
  }, [treeId]);

  // Geocode places with a 1 req/sec rate limit (Nominatim fair-use)
  useEffect(() => {
    if (!places || places.length === 0) return;
    let cancel = false;
    (async () => {
      setProgress({ done: 0, total: places.length });
      const results = [];
      for (let i = 0; i < places.length; i++) {
        if (cancel) return;
        const p = places[i];
        // Cache hits don't need rate-limiting
        const cache = loadCache();
        const cached = cache[p.label.trim().toLowerCase()];
        let geo;
        if (cached !== undefined) {
          geo = cached;
        } else {
          geo = await geocodeOnce(p.label);
          await new Promise((res) => setTimeout(res, 1050)); // Nominatim rate limit
        }
        if (geo) results.push({ ...p, lat: geo.lat, lon: geo.lon });
        setPins([...results]);
        setProgress({ done: i + 1, total: places.length });
      }
    })();
    return () => { cancel = true; };
  }, [places]);

  // Initialise Leaflet when we have at least one pin
  useEffect(() => {
    if (!mapRef.current || pins.length === 0) return;
    (async () => {
      const L = (await import("leaflet")).default;
      // Fix default icon (webpack-breaks-leaflet-images workaround)
      delete L.Icon.Default.prototype._getIconUrl;
      L.Icon.Default.mergeOptions({
        iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
        iconUrl:       "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
        shadowUrl:     "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
      });

      if (!mapInstance.current) {
        mapInstance.current = L.map(mapRef.current, { zoomControl: true });
        L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
          attribution: "© OpenStreetMap",
          maxZoom: 18,
        }).addTo(mapInstance.current);
      } else {
        mapInstance.current.eachLayer((l) => {
          if (l instanceof L.Marker || l instanceof L.CircleMarker) {
            mapInstance.current.removeLayer(l);
          }
        });
      }

      const bounds = L.latLngBounds([]);
      for (const pin of pins) {
        const colour = KIND_COLOR[pin.kind] || P.saffron;
        const marker = L.circleMarker([pin.lat, pin.lon], {
          radius: Math.min(6 + pin.count * 2, 18),
          color: colour,
          weight: 2,
          fillColor: colour,
          fillOpacity: 0.65,
        }).addTo(mapInstance.current);
        const names = (pin.member_names || []).slice(0, 5).join(", ") + (pin.count > 5 ? ` + ${pin.count - 5} more` : "");
        marker.bindPopup(
          `<div style="font-family:system-ui;min-width:180px">
            <div style="font-weight:600;color:${P.ink}">${pin.label}</div>
            <div style="font-size:11px;color:${P.inkSoft};text-transform:uppercase;letter-spacing:0.1em;margin-top:2px">${pin.kind} place · ${pin.count}</div>
            <div style="font-size:12px;color:${P.ink};margin-top:6px">${names}</div>
          </div>`
        );
        bounds.extend([pin.lat, pin.lon]);
      }
      if (bounds.isValid()) {
        mapInstance.current.fitBounds(bounds, { padding: [30, 30], maxZoom: 7 });
      }
    })();
  }, [pins]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (mapInstance.current) {
        mapInstance.current.remove();
        mapInstance.current = null;
      }
    };
  }, []);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-2 md:p-4"
      style={{ background: "rgba(67,26,4,0.6)", backdropFilter: "blur(6px)" }}
      onClick={onClose}
      data-testid="migration-map-modal"
    >
      <div
        className="w-full max-w-6xl h-[90vh] rounded-3xl overflow-hidden flex flex-col"
        style={{ background: P.parchment, boxShadow: "0 40px 80px -20px rgba(67,26,4,0.5)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="flex items-center justify-between px-5 lg:px-7 py-4"
          style={{ background: `linear-gradient(135deg, ${P.saffronBg} 0%, ${P.parchment} 100%)`, borderBottom: `1px solid ${P.line}` }}
        >
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: P.saffron, color: "#fff" }}>
              <MapPin className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <div className="font-display text-lg truncate" style={{ color: P.ink }}>
                {treeName ? `${treeName} on the map` : "Family migration map"}
              </div>
              <div className="text-xs" style={{ color: P.inkSoft }}>
                {places === null ? "Loading places…"
                  : places.length === 0 ? "No places recorded on any member yet"
                  : progress.done < progress.total
                      ? `Mapping ${progress.done} of ${progress.total} places…`
                      : `${pins.length} places plotted`}
              </div>
            </div>
          </div>
          <button onClick={onClose} aria-label="Close" className="w-9 h-9 rounded-full hover:bg-white/60 transition-colors flex items-center justify-center" style={{ color: P.inkSoft }} data-testid="migration-map-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 relative" style={{ background: "#e9e4dd" }}>
          {places !== null && places.length === 0 && !error && (
            <div className="absolute inset-0 flex items-center justify-center text-sm px-6 text-center" style={{ color: P.inkSoft }}>
              Add a <strong>birth place</strong> or <strong>current location</strong> to any member profile to see them on the map.
            </div>
          )}
          {error && (
            <div className="absolute inset-0 flex items-center justify-center text-sm" style={{ color: P.rose }}>
              {error}
            </div>
          )}
          {places === null && !error && (
            <div className="absolute inset-0 flex items-center justify-center gap-3 text-sm" style={{ color: P.inkSoft }}>
              <Loader2 className="w-5 h-5 animate-spin" style={{ color: P.saffron }} />
              Loading places…
            </div>
          )}
          <div ref={mapRef} className="absolute inset-0" style={{ visibility: pins.length > 0 ? "visible" : "hidden" }} />
        </div>

        {/* Legend */}
        <div
          className="px-5 lg:px-7 py-3 flex items-center gap-4 flex-wrap text-xs"
          style={{ background: "#fff", borderTop: `1px solid ${P.line}`, color: P.inkSoft }}
        >
          <span className="font-semibold tracking-widest uppercase text-[10px]">Legend:</span>
          <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-full" style={{ background: P.saffron }} /> Birthplace</span>
          <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-full" style={{ background: P.teal }} /> Current home</span>
          <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-full" style={{ background: P.rose }} /> Place of death</span>
          <span className="ml-auto">Pin size = number of family members</span>
        </div>
      </div>
    </div>
  );
}
